import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { MethodLogo } from '@/components/billing/MethodLogo';
import { formatXafAsEur, XAF_PER_EUR } from '@/lib/currencyConversion';
import { cn } from '@/lib/utils';
import type { PaymentMethodId, PaymentMethodOption } from '@/hooks/queries/usePaymentMethods';

interface PaymentMethodSelectorProps {
  value: PaymentMethodId;
  onChange: (method: PaymentMethodId) => void;
  /** XAF amount to convert for the card (EUR) hint. */
  amount: number;
  /** Backend availability; when undefined (loading/error) only mobile money shows. */
  methods?: PaymentMethodOption[];
  disabled?: boolean;
}

/**
 * Radio-card selector between Mobile Money (CinetPay) and bank card
 * (NytroPay, charged in EUR at the fixed BEAC rate).
 */
export function PaymentMethodSelector({
  value,
  onChange,
  amount,
  methods,
  disabled,
}: PaymentMethodSelectorProps) {
  const { t } = useTranslation('billing');

  const cardAvailable = methods?.find((m) => m.id === 'card')?.available ?? false;

  const options: {
    id: PaymentMethodId;
    label: string;
    description: string;
    logos: React.ReactNode;
  }[] = [
    {
      id: 'mobile_money',
      label: t('method_mobile_money'),
      description: t('method_mobile_money_desc'),
      logos: (
        <span className="flex items-center gap-1">
          <MethodLogo brand="orange" size="sm" />
          <MethodLogo brand="mtn" size="sm" />
          <MethodLogo brand="wave" size="sm" />
        </span>
      ),
    },
  ];

  if (cardAvailable) {
    options.push({
      id: 'card',
      label: t('method_card'),
      description: t('method_card_desc', {
        eur: formatXafAsEur(amount),
        rate: XAF_PER_EUR.toLocaleString('fr-FR'),
      }),
      logos: <MethodLogo brand="stripe" size="sm" />,
    });
  }

  return (
    <div
      role="radiogroup"
      aria-label={t('payment_method')}
      className={cn('grid gap-2', options.length > 1 ? 'sm:grid-cols-2' : 'grid-cols-1')}
    >
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className={cn(
              'flex flex-col gap-2 rounded-xl border bg-white p-4 text-left shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 dark:bg-card',
              selected
                ? 'border-lime-400 ring-1 ring-lime-400'
                : 'border-slate-200 hover:border-slate-300 dark:border-charcoal-800 dark:hover:border-charcoal-700',
              disabled && 'cursor-not-allowed opacity-60',
            )}
          >
            <span className="flex items-center justify-between gap-2">
              {option.logos}
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded-full border',
                  selected
                    ? 'border-lime-500 bg-lime-400 text-charcoal-950'
                    : 'border-slate-300 dark:border-charcoal-700',
                )}
                aria-hidden
              >
                {selected && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
            </span>
            <span className="text-sm font-semibold text-charcoal-900 dark:text-charcoal-50">
              {option.label}
            </span>
            <span className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              {option.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}
